import React, { useLayoutEffect, useRef, useState } from 'react';
import { Table, Tag, Tooltip, Input, Button, Space } from 'antd';
import type { TableColumnsType, TableColumnType } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { TRANSACTION_TYPES, BUY } from 'state';
import type { TopOrder } from 'state';
import { formatters } from 'services';
import type {
  Studio2Player,
  Studio2BlotterRow,
  Studio2OpenOrderRow,
  TradeAttribute,
} from '../../live/types';
import { money, int, signedMoney, ATTRIBUTE_META } from '../../helpers';
import type { RelativizeDate } from '../../helpers';

/** Measure a card body's height so its antd Table can own a SINGLE bounded
 *  vertical scroll (with a sticky header), instead of the table overflowing and
 *  the card body scrolling too — which stacks two scrollbars. Returns the height
 *  to pass as `scroll.y` (body area = container height − the table header). */
const useTableScrollY = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [y, setY] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const recompute = () => {
      const head = el.querySelector<HTMLElement>('.ant-table-thead');
      const headH = head?.offsetHeight ?? 39;
      const next = el.clientHeight - headH;
      setY(next > 0 ? next : undefined);
    };
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    recompute();
    return () => ro.disconnect();
  }, []);
  return { ref, y };
};

// "MM-DD HH:MM:SS" — seconds included so same-minute real-time executions are
// distinguishable and correctly ordered. (Real-time only; historical rows render
// via fmtDate.)
const fmtTime = (ts: string) => ts.slice(5, 19).replace('T', ' ');

// Historical sessions stamp orders with a date-only scenario date (time is
// always 00:00), so render "1st May 2026" (UTC-pinned) — same ordinal style as
// the session-timeline date.
const ordinal = (n: number) => {
  if ([11, 12, 13].includes(n % 100)) return 'th';
  return ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th';
};
const fmtDate = (ts: string) => {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  const day = d.getUTCDate();
  const month = new Intl.DateTimeFormat('en', {
    month: 'long',
    timeZone: 'UTC',
  }).format(d);
  return `${day}${ordinal(day)} ${month} ${d.getUTCFullYear()}`;
};

const txnTag = (t: number) => (
  <Tag color={t === BUY ? 'green' : 'red'} style={{ marginInlineEnd: 0 }}>
    {TRANSACTION_TYPES[t]}
  </Tag>
);

const attrTag = (a?: TradeAttribute | null) => {
  if (!a) return <span className="s2-muted">—</span>;
  const meta = ATTRIBUTE_META[a];
  return (
    <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>
      {meta.label}
    </Tag>
  );
};

const regimeTag = (
  mr?: Studio2BlotterRow['market_regime'],
  relativizeDate?: RelativizeDate,
) => {
  if (!mr) return <span className="s2-muted">—</span>;
  // Self+historical → "Day N"; trainer/RT → the raw scenario dates (unchanged).
  const d = (x: string) => relativizeDate?.(x) ?? x;
  return (
    <Tooltip title={`${d(mr.start_date)} → ${d(mr.end_date)}`}>
      <Tag color={mr.color} style={{ marginInlineEnd: 0 }}>
        {mr.label}
      </Tag>
    </Tooltip>
  );
};

const ellipsis = (text?: string) =>
  text ? (
    <Tooltip title={text}>
      <span
        style={{
          display: 'inline-block',
          maxWidth: 160,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          verticalAlign: 'bottom',
        }}
      >
        {text}
      </span>
    </Tooltip>
  ) : (
    <span className="s2-muted">—</span>
  );

// Rationale cell: system-placed orders carry "Automated order" — show "—" for
// those (no trainee intent to read), otherwise the (ellipsised) free text.
const rationaleCell = (text?: string) =>
  ellipsis(text === 'Automated order' ? undefined : text);

// Free-text "contains" search on the Asset column, matched against its
// displayed value (the ticker), case-insensitive. Shared by both tables.
const assetSearchProps: Pick<
  TableColumnType<{ underlying_asset_ticker: string }>,
  'filterDropdown' | 'filterIcon' | 'onFilter'
> = {
  filterDropdown: ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
  }) => (
    <div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()}>
      <div style={{ marginBottom: 8 }}>
        <Input
          autoFocus
          allowClear
          placeholder="Search asset"
          value={selectedKeys[0] as string}
          onChange={e =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
          style={{ width: 180 }}
        />
      </div>
      <Space>
        <Button
          type="primary"
          size="small"
          icon={<SearchOutlined />}
          onClick={() => confirm()}
          style={{ width: 84 }}
        >
          Search
        </Button>
        <Button
          size="small"
          onClick={() => {
            clearFilters?.();
            confirm();
          }}
          style={{ width: 84 }}
        >
          Reset
        </Button>
      </Space>
    </div>
  ),
  filterIcon: filtered => (
    <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
  ),
  onFilter: (value, record) =>
    record.underlying_asset_ticker
      .toLowerCase()
      .includes(String(value).toLowerCase()),
};

const blotterCols = (
  isHistorical: boolean,
  relativizeDate: RelativizeDate | undefined,
  assetFilter: React.Key[],
  onAssetClick: (asset: string) => void,
  hasSimplifiedOptions: boolean,
): TableColumnsType<Studio2BlotterRow> => [
  {
    title: 'Asset',
    dataIndex: 'underlying_asset_ticker',
    fixed: 'left',
    width: 96,
    ...assetSearchProps,
    // Controlled, so a cell click can set it and the header search stays in sync
    // via the table's onChange.
    filteredValue: assetFilter.length ? assetFilter : null,
    // Click any Asset cell to filter the blotter to that asset (click the active
    // one again to clear) — same effect as typing it into the Asset search.
    onCell: record => ({
      onClick: () => onAssetClick(record.underlying_asset_ticker),
      style: { cursor: 'pointer' },
    }),
    // Visual cue that the cell is clickable (dotted underline) + a tooltip that
    // explains the action and reflects the toggle state.
    render: (ticker: string) => {
      const active = assetFilter.length === 1 && assetFilter[0] === ticker;
      return (
        <Tooltip
          placement="left"
          title={
            active
              ? 'Filtering by this asset — click to clear'
              : 'Click to filter the blotter by this asset'
          }
        >
          <span style={{ borderBottom: '1px dotted currentColor' }}>
            {ticker}
          </span>
        </Tooltip>
      );
    },
  },
  {
    title: 'Txn',
    dataIndex: 'transaction',
    width: hasSimplifiedOptions ? 112 : 84,
    render: (transaction, record) =>
      record.option_action || txnTag(transaction),
  },
  {
    title: 'Trade Attribute',
    dataIndex: 'attribute',
    width: 120,
    render: attrTag,
  },
  {
    title: 'Price',
    dataIndex: 'open_price',
    width: 80,
    align: 'right',
    render: (v: number, record) =>
      record.is_simplified_option ? '-' : formatters.commas2Digits(v),
  },
  {
    // Unit (millions) lives in the header; cells are plain numbers. Value is in
    // the instrument's own currency (the Ccy column is intentionally dropped).
    title: 'Value (M)',
    dataIndex: 'value',
    width: 90,
    align: 'right',
    render: (value, record) =>
      record.is_simplified_option
        ? '-'
        : formatters.millionNoUnit(value),
  },
  ...(hasSimplifiedOptions
    ? [
        {
          title: 'Premium',
          dataIndex: 'option_premium',
          width: 100,
          align: 'right' as const,
          render: (value: number | string | null, record: Studio2BlotterRow) =>
            value == null
              ? '-'
              : `${formatters.commas2Digits(value)} ${record.currency}`,
        },
        {
          title: 'Expiry days',
          dataIndex: 'option_expiry_days',
          width: 92,
          align: 'right' as const,
          render: (value: number | null) => value ?? '-',
        },
        {
          title: 'Payoff',
          dataIndex: 'option_payoff',
          width: 100,
          align: 'right' as const,
          render: (value: number | string | null, record: Studio2BlotterRow) =>
            value == null
              ? '-'
              : `${formatters.commas2Digits(value)} ${record.currency}`,
        },
      ]
    : []),
  // Market-regime tags are historical-only: the regime segments are built on the
  // scenario-date grid, so on real-time sessions every row renders "—". Hide the
  // whole column for RT (isHistorical === false).
  ...(isHistorical
    ? [
        {
          title: 'Market regime',
          dataIndex: 'market_regime',
          width: 132,
          render: (mr: Studio2BlotterRow['market_regime']) =>
            regimeTag(mr, relativizeDate),
        },
      ]
    : []),
  {
    title: 'Rationale',
    dataIndex: 'investment_rationale',
    width: 200,
    render: rationaleCell,
  },
  {
    title: relativizeDate ? 'Day' : isHistorical ? 'Date' : 'Time',
    dataIndex: 'timestamp',
    width: isHistorical ? 116 : 92,
    render: (ts: string) =>
      relativizeDate?.(ts) ?? (isHistorical ? fmtDate(ts) : fmtTime(ts)),
  },
];

const openCols = (
  isHistorical: boolean,
  relativizeDate?: RelativizeDate,
): TableColumnsType<Studio2OpenOrderRow> => [
  {
    title: 'Asset',
    dataIndex: 'underlying_asset_ticker',
    fixed: 'left',
    width: 96,
    ...assetSearchProps,
  },
  { title: 'Type', dataIndex: 'resourcetype', width: 120 },
  { title: 'Txn', dataIndex: 'transaction', width: 84, render: txnTag },
  { title: 'Ccy', dataIndex: 'currency', width: 56 },
  {
    title: 'Order price',
    dataIndex: 'limit_price',
    width: 90,
    align: 'right',
    render: (v: number) => formatters.commas2Digits(v),
  },
  {
    title: 'Qty',
    dataIndex: 'quantity',
    width: 72,
    align: 'right',
    render: int,
  },
  {
    title: 'Value',
    dataIndex: 'value',
    width: 84,
    align: 'right',
    render: money,
  },
  {
    title: relativizeDate ? 'Day' : isHistorical ? 'Date' : 'Time',
    dataIndex: 'timestamp',
    width: isHistorical ? 116 : 92,
    render: (ts: string) =>
      relativizeDate?.(ts) ?? (isHistorical ? fmtDate(ts) : fmtTime(ts)),
  },
  {
    title: 'Rationale',
    dataIndex: 'investment_rationale',
    width: 170,
    render: rationaleCell,
  },
];

const BestWorst: React.FC<{
  variant: 'win' | 'lose' | 'long' | 'short';
  label: string;
  instrument: string;
  value: number;
  meta: string;
  // Optional second meta line (e.g. "opened … · closed …" for winner/loser).
  dates?: string;
}> = ({ variant, label, instrument, value, meta, dates }) => (
  <div className={`studio2__bw-card studio2__bw-card--${variant}`}>
    <span className="studio2__bw-label">{label}</span>
    <div className="studio2__bw-main">
      <span className="studio2__bw-inst">{instrument}</span>
      <span className={`studio2__bw-val ${value >= 0 ? 's2-pos' : 's2-neg'}`}>
        {signedMoney(value)}
      </span>
    </div>
    <span className="studio2__bw-meta">{meta}</span>
    {dates ? <span className="studio2__bw-meta">{dates}</span> : null}
  </div>
);

const DecisionsPanel: React.FC<{
  player: Studio2Player;
  isHistorical: boolean;
  // Self + historical only: relativises every date this panel renders to
  // "Day N". Absent (trainer/cohort, or real-time) → existing date/time output.
  relativizeDate?: RelativizeDate;
}> = ({ player, isHistorical, relativizeDate }) => {
  const blotterScroll = useTableScrollY();
  const openScroll = useTableScrollY();
  // Click-to-filter for the blotter's Asset column: clicking a cell filters to
  // that asset (same as typing it into the Asset search); clicking the active
  // asset again clears it. Controlled so both entry points stay in sync.
  const [assetFilter, setAssetFilter] = useState<React.Key[]>([]);
  const onAssetClick = (asset: string) =>
    setAssetFilter(prev =>
      prev.length === 1 && prev[0] === asset ? [] : [asset],
    );
  const inv = player.report.investments;
  const w = inv.biggest_winner;
  const l = inv.biggest_loser;
  const lng = inv.top_long_position;
  const sht = inv.top_short_position;

  // Best/worst card date: self+historical → "Day N" (the realized scenario
  // date, relativised); trainer historical → "1st May 2026"; real-time → real
  // timestamp as "05-01 06:04".
  const cardDate = (real: string, scenario?: string | null) =>
    relativizeDate?.(scenario || real) ??
    (isHistorical ? fmtDate(scenario || real) : fmtTime(real));

  // Winner/loser are realizing orders: `opened_at` is their position's open
  // date (added by the backend), `executed_at` is the close. Pre-recompute
  // reports lack `opened_at` — fall back to showing the close date alone.
  const tradeDates = (o: TopOrder) => {
    const close = cardDate(o.executed_at, o.executed_at_scenario_date);
    return o.opened_at
      ? `opened ${cardDate(o.opened_at, o.opened_at_scenario_date)} · closed ${close}`
      : close;
  };

  return (
    <div className="studio2__decisions">
      <div className="studio2__decisions-left">
        <div className="studio2__card">
          <div className="studio2__card-head">
            <span className="studio2__card-title">
              Blotter
              {player.blotter.length > 0 && (
                <span
                  className="s2-muted"
                  style={{ fontWeight: 500, textTransform: 'none' }}
                >
                  {' — '}click an asset to filter
                </span>
              )}
            </span>
            <span className="s2-muted" style={{ fontSize: 11 }}>
              {player.blotter.length} executed
            </span>
          </div>
          <div
            ref={blotterScroll.ref}
            className="studio2__card-body studio2__card-body--pad0"
            style={{ overflow: 'hidden' }}
          >
            <Table
              size="small"
              rowKey="key"
              columns={blotterCols(
                isHistorical,
                relativizeDate,
                assetFilter,
                onAssetClick,
                player.blotter.some(row => row.is_simplified_option),
              )}
              dataSource={player.blotter}
              pagination={false}
              scroll={{ x: 'max-content', y: blotterScroll.y }}
              onChange={(_pagination, filters) =>
                setAssetFilter(
                  (filters.underlying_asset_ticker as React.Key[]) ?? [],
                )
              }
            />
          </div>
        </div>

        <div className="studio2__card">
          <div className="studio2__card-head">
            <span className="studio2__card-title">Open orders</span>
            <span className="s2-muted" style={{ fontSize: 11 }}>
              {player.open_orders.length} pending
            </span>
          </div>
          <div
            ref={openScroll.ref}
            className="studio2__card-body studio2__card-body--pad0"
            style={{ overflow: 'hidden' }}
          >
            <Table
              size="small"
              rowKey="key"
              columns={openCols(isHistorical, relativizeDate)}
              dataSource={player.open_orders}
              pagination={false}
              scroll={{ x: 'max-content', y: openScroll.y }}
            />
          </div>
        </div>
      </div>

      <div className="studio2__bestworst">
        <BestWorst
          variant="win"
          label="Biggest winner"
          instrument={w.instrument}
          value={w.value}
          meta={`${int(w.quantity)} @ ${formatters.commas2Digits(w.open_price)} → ${formatters.commas2Digits(
            w.close_price,
          )}`}
          dates={tradeDates(w)}
        />
        <BestWorst
          variant="lose"
          label="Biggest loser"
          instrument={l.instrument}
          value={l.value}
          meta={`${int(l.quantity)} @ ${formatters.commas2Digits(l.open_price)} → ${formatters.commas2Digits(
            l.close_price,
          )}`}
          dates={tradeDates(l)}
        />
        <BestWorst
          variant="long"
          label="Top long position"
          instrument={lng.instrument}
          value={lng.value}
          meta={`opened ${cardDate(lng.created_at, lng.created_at_scenario_date)} · ${
            lng.closed_at
              ? `closed ${cardDate(lng.closed_at, lng.closed_at_scenario_date)}`
              : 'still open'
          }`}
        />
        <BestWorst
          variant="short"
          label="Top short position"
          instrument={sht.instrument}
          value={sht.value}
          meta={`opened ${cardDate(sht.created_at, sht.created_at_scenario_date)} · ${
            sht.closed_at
              ? `closed ${cardDate(sht.closed_at, sht.closed_at_scenario_date)}`
              : 'still open'
          }`}
        />
      </div>
    </div>
  );
};

export default DecisionsPanel;
